import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Loader2, X, Pencil, FileDown } from "lucide-react";
import api from "@/lib/api";
import { downloadFile } from "@/lib/download";

const EMPTY = { title: "", description: "", video_url: "", duration: "", order: 0, is_preview: false };

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => {
      const cs = r.data.filter((p) => p.category_slug === "courses");
      setCourses(cs);
      if (cs[0]) setCourseId(cs[0].id);
    }).catch(() => {});
  }, []);

  const loadLessons = (cid) => api.get(`/admin/courses/${cid}/lessons`).then((r) => setLessons(r.data)).catch(() => {});
  useEffect(() => { if (courseId) loadLessons(courseId); }, [courseId]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, product_id: courseId, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/lessons/${editing.id}`, payload);
      else await api.post("/admin/lessons", payload);
      toast.success("Lesson saved");
      setEditing(null);
      loadLessons(courseId);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    await api.delete(`/admin/lessons/${id}`);
    toast.success("Deleted");
    loadLessons(courseId);
  };

  return (
    <div data-testid="admin-courses">
      <div className="mb-6">
        <p className="overline mb-2">Admin · Content</p>
        <h1 className="font-editorial text-3xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-4xl">Courses</h1>
        <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Add gated video lessons. Create the course itself under Products (category “courses”).</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <select value={courseId} onChange={(e) => setCourseId(e.target.value)} data-testid="course-select"
          className="rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)]">
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        {courseId && <button onClick={() => setEditing({ ...EMPTY, order: lessons.length })} data-testid="lesson-new" className="btn-edge text-sm"><Plus size={14} /> New lesson</button>}
      </div>

      {courses.length === 0 ? (
        <div className="card-edge p-8 text-[color:var(--ink-muted)]">No course products yet. Create one in Products with category “courses”.</div>
      ) : (
        <div className="grid gap-2">
          {lessons.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--ink-hairline)] bg-white p-4">
              <div>
                <p className="font-medium text-[color:var(--ink)]">{l.order + 1}. {l.title} {l.is_preview && <span className="chip chip-amber ml-2">preview</span>}</p>
                <p className="text-[11px] text-[color:var(--ink-muted)]">{l.duration || "—"} · {l.video_url || "no video"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(l)} data-testid={`lesson-edit-${l.id}`} className="rounded-md border border-[color:var(--ink-hairline)] p-2 text-[color:var(--ink-body)] hover:border-[color:var(--ink)]"><Pencil size={14} /></button>
                <button onClick={() => remove(l.id)} data-testid={`lesson-delete-${l.id}`} className="rounded-md border border-[color:var(--ink-hairline)] p-2 text-[color:var(--alert)] hover:border-[color:var(--alert)]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {lessons.length === 0 && <div className="card-edge p-8 text-[color:var(--ink-muted)]">No lessons in this course yet.</div>}
        </div>
      )}

      {courses.length > 0 && courseId && <CourseDocs courseId={courseId} />}

      {courses.length > 0 && courseId && <CourseCertificates courseId={courseId} courses={courses} />}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-lg rounded-lg border border-[color:var(--ink-hairline)] bg-[color:var(--paper)] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-editorial text-xl font-semibold text-[color:var(--ink)]">{editing.id ? "Edit lesson" : "New lesson"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="grid gap-4">
              {[["Title", "title"], ["Description", "description"], ["Video URL (YouTube/Vimeo/MP4/Bunny)", "video_url"], ["Duration (e.g. 12 min)", "duration"], ["Order", "order"]].map(([label, k]) => (
                <div key={k}>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">{label}</label>
                  <input value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} data-testid={`lesson-${k}`}
                    className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
                </div>
              ))}
              <label className="flex items-center gap-2 text-sm text-[color:var(--ink-body)]">
                <input type="checkbox" checked={!!editing.is_preview} onChange={(e) => setEditing({ ...editing, is_preview: e.target.checked })} data-testid="lesson-preview" />
                Free preview (visible without purchase)
              </label>
            </div>
            <button disabled={saving} data-testid="lesson-save" className="btn-edge mt-6 w-full justify-center">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save lesson
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function CourseDocs({ courseId }) {
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/courses/${courseId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/courses/${courseId}/documents${title ? `?title=${encodeURIComponent(title)}` : ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setTitle(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/admin/courses/documents/${id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div className="mt-10" data-testid="course-docs-admin">
      <p className="overline mb-2">Course materials (documents)</p>
      <p className="mb-3 text-sm text-[color:var(--ink-muted)]">Upload PDFs, worksheets or notes. Enrolled students can read PDFs online and download any file.</p>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional title" data-testid="course-doc-title"
          className="rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)]" />
        <label className="btn-edge cursor-pointer text-sm">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Upload document
          <input type="file" hidden onChange={upload} data-testid="course-doc-upload" accept=".pdf,.doc,.docx,.txt,.epub,.csv,.xlsx,.pptx" />
        </label>
      </div>
      <div className="grid gap-2">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-md border border-[color:var(--ink-hairline)] bg-white p-3 text-sm" data-testid={`course-doc-row-${d.id}`}>
            <span className="text-[color:var(--ink)]">{d.title} <span className="text-[11px] text-[color:var(--ink-muted)]">· {d.ext?.toUpperCase()} · {(d.size / 1024 / 1024).toFixed(1)} MB{d.viewable ? " · readable online" : ""}</span></span>
            <button onClick={() => remove(d.id)} data-testid={`course-doc-del-${d.id}`} className="text-[color:var(--alert)] hover:opacity-70"><Trash2 size={14} /></button>
          </div>
        ))}
        {docs.length === 0 && <p className="text-sm text-[color:var(--ink-muted)]">No documents yet.</p>}
      </div>
    </div>
  );
}

function CourseCertificates({ courseId, courses }) {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const course = courses.find((c) => c.id === courseId);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const [instr, setInstr] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [sigUrl, setSigUrl] = useState("");
  const [sigBusy, setSigBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/certificates").then((r) => setCerts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    setInstr(course?.cert_instructor_name || "");
    setSigUrl(course?.cert_signature_path ? `${BACKEND}/api/media/${course.cert_signature_path}` : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try { await api.patch(`/admin/courses/${courseId}/certificate-meta`, { instructor_name: instr }); toast.success("Instructor name saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSavingMeta(false); }
  };
  const uploadSig = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSigBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/courses/${courseId}/certificate-signature`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSigUrl(`${BACKEND}${data.signature_url}`);
      toast.success("Signature uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setSigBusy(false); e.target.value = ""; }
  };
  const removeSig = async () => {
    if (!window.confirm("Remove the signature image?")) return;
    try { await api.delete(`/admin/courses/${courseId}/certificate-signature`); setSigUrl(""); toast.success("Signature removed"); }
    catch (e) { toast.error("Failed"); }
  };

  const rows = certs.filter((c) => c.product_id === courseId);
  const toggle = async (c) => {
    const action = c.revoked ? "restore" : "revoke";
    if (action === "revoke" && !window.confirm(`Revoke ${c.user_name}'s certificate? Its public link will stop working (use after a refund).`)) return;
    try {
      await api.post(`/admin/certificates/${c.code}/${action}`);
      toast.success(c.revoked ? "Certificate restored" : "Certificate revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-10" data-testid="course-certs-admin">
      <div className="mb-6 card-edge p-5" data-testid="course-cert-signature-form">
        <p className="overline mb-2">Certificate signature</p>
        <p className="mb-4 text-sm text-[color:var(--ink-muted)]">Add an instructor name and signature image — they appear on every certificate for this course for extra credibility.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Instructor name</label>
            <div className="flex gap-2">
              <input value={instr} onChange={(e) => setInstr(e.target.value)} data-testid="cert-instructor-input" placeholder="e.g. Henry Kimani"
                className="flex-1 rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
              <button onClick={saveMeta} disabled={savingMeta} data-testid="cert-instructor-save" className="btn-edge text-sm">
                {savingMeta ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Signature image (PNG/JPG/SVG, ≤5MB)</label>
            <div className="flex items-center gap-3">
              {sigUrl ? (
                <img src={sigUrl} alt="signature" className="h-10 w-auto max-w-[140px] rounded border border-[color:var(--ink-hairline)] bg-white object-contain p-1" data-testid="cert-signature-preview" />
              ) : <span className="text-xs text-[color:var(--ink-muted)]">None uploaded</span>}
              <label className="btn-edge cursor-pointer text-sm" data-testid="cert-signature-upload">
                {sigBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {sigUrl ? "Replace" : "Upload"}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={uploadSig} />
              </label>
              {sigUrl && <button onClick={removeSig} data-testid="cert-signature-remove" className="rounded-md border border-[color:var(--ink-hairline)] p-2 text-[color:var(--alert)] hover:border-[color:var(--alert)]"><Trash2 size={14} /></button>}
            </div>
          </div>
        </div>
      </div>

      <p className="overline mb-2">Certificates issued{course ? ` · ${course.title}` : ""}</p>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[color:var(--ink-muted)]">Students earn a certificate at 100% completion. Revoke one if the purchase was later refunded — its public link stops working immediately.</p>
        <button onClick={() => downloadFile("/admin/certificates/export.csv", "certificates.csv")} data-testid="cert-export-csv" className="btn-edge-ghost shrink-0 text-xs"><FileDown size={13} /> Export CSV</button>
      </div>
      <div className="grid gap-2">
        {loading && <p className="text-sm text-[color:var(--ink-muted)]">Loading…</p>}
        {!loading && rows.length === 0 && <p className="text-sm text-[color:var(--ink-muted)]">No certificates issued for this course yet.</p>}
        {rows.map((c) => (
          <div key={c.code} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[color:var(--ink-hairline)] bg-white p-3 text-sm" data-testid={`cert-row-${c.code}`}>
            <div>
              <p className="text-[color:var(--ink)]">{c.user_name} {c.revoked && <span className="chip chip-amber ml-2">revoked</span>}</p>
              <p className="text-[11px] text-[color:var(--ink-muted)]">ID {c.code} · issued {new Date(c.issued_at).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2">
              <a href={`/certificate/${c.code}`} target="_blank" rel="noreferrer" className="rounded-md border border-[color:var(--ink-hairline)] px-3 py-1.5 text-xs text-[color:var(--ink-body)] hover:border-[color:var(--evergreen)]">View</a>
              <button onClick={() => toggle(c)} data-testid={`cert-toggle-${c.code}`}
                className={`rounded-md border px-3 py-1.5 text-xs ${c.revoked ? "border-[color:var(--evergreen)] text-[color:var(--evergreen)]" : "border-[color:var(--alert)] text-[color:var(--alert)]"} hover:opacity-70`}>
                {c.revoked ? "Restore" : "Revoke"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
